import React, { useState, Fragment, useEffect } from "react";
import { PuzzlePieceIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { useStore } from "../contexts/StoreContext";
import IntegrationManageModal from "../components/IntegrationManageModal";
import IntegrationCard from "./integrations/components/IntegrationCard";
import { useIntegrationsData } from "../hooks/useIntegrationsData";
import { usePlanAlert } from "../contexts/PlanAlertContext";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { disconnectIntegration } from "../api/mutations";
import DeleteAlert from "../components/DeleteAlert";

/**
 * Integrations page component for managing third-party integrations
 * This component has been refactored to use:
 * - Custom hook (useIntegrationsData) for data fetching and state management
 * - Extracted components for better code organization and reusability
 * - React.memo for performance optimization
 * - PlanAlertContext for consistent plan alert behavior
 *
 */
const Integrations = () => {
  const { t } = useLanguage();
  const { isStoreLoading, selectedStore } = useStore();
  const { showPlanAlert } = usePlanAlert();
  const location = useLocation();
  const navigate = useNavigate();

  // Read tag from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const tagFromURL = queryParams.get("tag");

  const [selectedTag, setSelectedTag] = useState(tagFromURL || "all");

  // State for disconnect confirmation modal
  const [isDisconnectAlertOpen, setIsDisconnectAlertOpen] = useState(false);
  const [disconnectingIntegration, setDisconnectingIntegration] =
    useState(null);
  const [isDisconnectLoading, setIsDisconnectLoading] = useState(false);

  const {
    // Data
    integrationList,
    twilioData,
    sendgridData,
    mailchimpData,
    mailgunData,
    mandrillData,
    shopifyData,

    // UI State
    isModalOpen,
    setIsModalOpen,
    integrationType,

    // Loading state
    isIntegrationsLoading,

    // Store data
    storeId,

    // Handlers
    handleIntegrationSuccess,
  } = useIntegrationsData();

  // Extract unique tags from integration list
  const allTags = ["all"];
  integrationList.forEach((integration) => {
    if (integration.tags && integration.tags.length > 0) {
      integration.tags.forEach((tag) => {
        if (!allTags.includes(tag)) {
          allTags.push(tag);
        }
      });
    }
  });

  // Update selected tag when URL changes
  useEffect(() => {
    const tag = queryParams.get("tag");
    if (tag && allTags.includes(tag)) {
      setSelectedTag(tag);
    } else if (!tag && location.pathname === "/integrations") {
      setSelectedTag("all");
    }
  }, [location.search, allTags, location.pathname]);

  // Handle tag selection change
  const handleTagChange = (tag) => {
    setSelectedTag(tag);

    // Update URL with the selected tag
    if (tag === "all") {
      navigate("/integrations");
    } else {
      navigate(`/integrations?tag=${tag}`);
    }
  };

  // Sort integrations to put "coming soon" ones at the bottom
  const sortIntegrations = (integrations) => {
    return [...integrations].sort((a, b) => {
      // If both are coming soon or both are not coming soon, maintain original order
      if (a.status === b.status) return 0;
      // If a is coming soon, it should come after b
      if (a.status === "soon") return 1;
      // If b is coming soon, it should come after a
      if (b.status === "soon") return -1;
      return 0;
    });
  };

  // Filter integrations based on selected tag
  const filteredIntegrations = sortIntegrations(
    selectedTag === "all"
      ? integrationList
      : integrationList.filter(
          (integration) =>
            integration.tags && integration.tags.includes(selectedTag),
        ),
  );

  // Check if the store has advanced plan
  const hasAdvancedPlan = selectedStore?.current_plan === "advanced";

  // Handle integration button click with plan check
  const handleIntegrationClick = (type, onClick) => {
    if (hasAdvancedPlan) {
      // If they have advanced plan, proceed normally
      onClick();
    } else {
      // Show plan alert using the context
      showPlanAlert({
        title: t("common.planAlertTitle"),
        description: t("common.planAlertDescription", {
          plan: "Advanced",
        }),
        plan: "Advanced",
      });
    }
  };

  // Handle opening the disconnect modal
  const handleOpenDisconnectAlert = (integration) => {
    setDisconnectingIntegration(integration);
    setIsDisconnectAlertOpen(true);
  };

  // Handle disconnecting an integration
  const handleDisconnect = async () => {
    if (!disconnectingIntegration) return;

    try {
      setIsDisconnectLoading(true);

      await disconnectIntegration({
        storeId: storeId,
        integrationId: disconnectingIntegration.id,
      });

      // Call the success handler to refresh the data
      handleIntegrationSuccess();

      // Close the modal and reset state
      setIsDisconnectAlertOpen(false);
      setDisconnectingIntegration(null);
      setIsDisconnectLoading(false);
    } catch (error) {
      console.error("Failed to disconnect integration:", error);
      setIsDisconnectLoading(false);
      // Keep the modal open to show the error
    }
  };

  // Map integration types to their respective data
  const integrationDataMap = {
    1: twilioData,
    2: sendgridData,
    3: mailchimpData,
    4: mailgunData,
    7: shopifyData,
    11: mandrillData,
  };

  const getIntegrationData = (type) => {
    switch (type) {
      case 1:
        return twilioData;
      case 2:
        return sendgridData;
      case 3:
        return mailchimpData;
      case 4:
        return mailgunData;
      case 7:
        return shopifyData;
      case 11:
        return mandrillData;
      default:
        return null;
    }
  };

  if (isStoreLoading || isIntegrationsLoading) {
    return (
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        icon={PuzzlePieceIcon}
        title={t("integrations.list.title")}
        description={t("integrations.list.description")}
        rightSection={
          <div className="mt-4 sm:mt-0 sm:ml-16">
            <Listbox value={selectedTag} onChange={handleTagChange}>
              <div className="relative mt-1 min-w-[180px]">
                <Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-xs border border-gray-300 focus:border-indigo-500 focus:outline-hidden focus:ring-1 focus:ring-indigo-500 sm:text-sm">
                  <span className="block truncate capitalize">
                    {selectedTag === "all"
                      ? t("integrations.filter.allTags")
                      : selectedTag}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm">
                    {allTags.map((tag, tagIdx) => (
                      <Listbox.Option
                        key={tagIdx}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active
                              ? "bg-indigo-100 text-indigo-900"
                              : "text-gray-900"
                          }`
                        }
                        value={tag}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate capitalize ${
                                selected ? "font-medium" : "font-normal"
                              }`}
                            >
                              {tag === "all"
                                ? t("integrations.filter.allTags")
                                : tag}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
        }
      />

      {/* Integration cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 lg:gap-8">
        {filteredIntegrations.map((integration) => (
          <IntegrationCard
            id={integration.id}
            key={integration.key}
            integration={integration}
            t={t}
            onButtonClick={() =>
              handleIntegrationClick(integration.id, integration.onClick)
            }
            onDisconnect={() => handleOpenDisconnectAlert(integration)}
          />
        ))}
      </div>

      {/* Integration Modal */}
      <IntegrationManageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        integrationType={integrationType}
        integrationData={getIntegrationData(integrationType)}
        storeId={storeId}
        onSuccess={handleIntegrationSuccess}
        integrationList={integrationList}
      />

      {/* Disconnect Confirmation Alert */}
      <DeleteAlert
        isOpen={isDisconnectAlertOpen}
        onClose={() => {
          setIsDisconnectAlertOpen(false);
          setDisconnectingIntegration(null);
        }}
        onConfirm={handleDisconnect}
        title={t("integrations.disconnectConfirmation.title")}
        description={
          t("integrations.disconnectConfirmation.message", {
            integration: disconnectingIntegration?.title || "",
          }) +
          (disconnectingIntegration
            ? "<br /><br />" + t("integrations.disconnectConfirmation.warning")
            : "")
        }
        buttonText={t("integrations.disconnect")}
        isLoading={isDisconnectLoading}
      />
    </div>
  );
};

export default React.memo(Integrations);
